import React, { useState } from 'react'
import { Calendar, Clock, Edit, Eye, MoreVertical, Trash, Copy, Send, Loader2, CheckCircle2, AlertCircle, RefreshCw, ExternalLink } from 'lucide-react'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import type { SocialPost, SocialProfile } from '@/hooks/useSocialMedia'
import { POST_STATUS, STATUS_LABELS, STATUS_COLORS, getAvailableActions, getTimeRemaining, type PostStatus } from '@/constants/socialPostStatus'
import StatusTransition from './StatusTransition'

interface PostListProps {
  posts: SocialPost[]
  profiles: SocialProfile[]
  onEdit?: (post: SocialPost) => void
  onDelete?: (postId: string) => void
  onDuplicate?: (post: SocialPost) => void
  onPublish?: (post: SocialPost) => void
  onRetry?: (post: SocialPost) => void
  onViewOnPlatform?: (post: SocialPost) => void
}

export default function PostList({ posts, profiles, onEdit, onDelete, onDuplicate, onPublish, onRetry, onViewOnPlatform }: PostListProps) {
  const [loadingActions, setLoadingActions] = useState<Record<string, string>>({}) // postId -> action
  
  const getProfile = (profileId: string | null) => {
    if (!profileId) return null
    return profiles.find(p => p.id === profileId)
  }

  const getStatusColor = (status: string) => {
    // Handle legacy QUEUED status
    if (status === 'QUEUED') return 'bg-purple-500'
    return STATUS_COLORS[status as keyof typeof STATUS_COLORS] || 'bg-gray-500'
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case POST_STATUS.SCHEDULED: return <Clock className="h-3 w-3" />
      case POST_STATUS.PROCESSING: return <Loader2 className="h-3 w-3 animate-spin" />
      case POST_STATUS.POSTED: return <CheckCircle2 className="h-3 w-3" />
      case POST_STATUS.ERROR: return <AlertCircle className="h-3 w-3" />
      default: return null
    }
  }
  
  const getStatusLabel = (status: string) => {
    // Handle legacy QUEUED status
    if (status === 'QUEUED') return 'Queued'
    return STATUS_LABELS[status as keyof typeof STATUS_LABELS] || status
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No posts found. Create your first post to get started.</p>
      </div>
    )
  }

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post) => {
        const profile = getProfile(post.profile_id)
        
        return (
          <Card 
            key={post.id} 
            className={cn(
              "overflow-hidden transition-all duration-300",
              post.status === POST_STATUS.PROCESSING && "ring-2 ring-yellow-400 ring-offset-2 animate-pulse"
            )}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {profile?.platform || 'No Profile'}
                    </Badge>
                    <StatusTransition status={post.status as PostStatus} />
                  </div>
                  {profile && (
                    <p className="text-sm text-muted-foreground">{profile.name}</p>
                  )}
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {(() => {
                      const actions = getAvailableActions(post.status as any);
                      const hasActions = [];
                      
                      // Edit action
                      if (onEdit && actions.canEdit) {
                        hasActions.push(
                          <DropdownMenuItem key="edit" onClick={() => onEdit(post)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        );
                      }
                      
                      // Duplicate action (always available)
                      if (onDuplicate) {
                        hasActions.push(
                          <DropdownMenuItem key="duplicate" onClick={() => onDuplicate(post)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                        );
                      }
                      
                      // Publish Now action
                      if (onPublish && actions.canPublishNow) {
                        const isPastDue = post.scheduled_time && new Date(post.scheduled_time) <= new Date();
                        hasActions.push(
                          <DropdownMenuItem 
                            key="publish"
                            onClick={async () => {
                              setLoadingActions(prev => ({ ...prev, [post.id]: 'publish' }))
                              try {
                                await onPublish(post)
                              } finally {
                                setLoadingActions(prev => {
                                  const { [post.id]: _, ...rest } = prev
                                  return rest
                                })
                              }
                            }}
                            disabled={loadingActions[post.id] === 'publish'}
                          >
                            {loadingActions[post.id] === 'publish' ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Publishing...
                              </>
                            ) : (
                              <>
                                <Send className="h-4 w-4 mr-2" />
                                {post.status === POST_STATUS.SCHEDULED ? 
                                  (isPastDue ? 'Publish Now (Past Due)' : 'Publish Now') : 
                                  'Publish to Instagram'}
                              </>
                            )}
                          </DropdownMenuItem>
                        );
                      }
                      
                      // Retry action (for errors)
                      if (onRetry && actions.canRetry) {
                        hasActions.push(
                          <DropdownMenuItem 
                            key="retry"
                            onClick={async () => {
                              setLoadingActions(prev => ({ ...prev, [post.id]: 'retry' }))
                              try {
                                await onRetry(post)
                              } finally {
                                setLoadingActions(prev => {
                                  const { [post.id]: _, ...rest } = prev
                                  return rest
                                })
                              }
                            }}
                            disabled={loadingActions[post.id] === 'retry'}
                          >
                            {loadingActions[post.id] === 'retry' ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Retrying...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Retry Publishing
                              </>
                            )}
                          </DropdownMenuItem>
                        );
                      }
                      
                      // View on Platform action
                      if (onViewOnPlatform && actions.canViewOnPlatform) {
                        hasActions.push(
                          <DropdownMenuItem key="view" onClick={() => onViewOnPlatform(post)}>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View on Instagram
                          </DropdownMenuItem>
                        );
                      }
                      
                      // Add separator if there are actions above and delete below
                      if (hasActions.length > 0 && onDelete && actions.canDelete) {
                        hasActions.push(<DropdownMenuSeparator key="separator" />);
                      }
                      
                      // Delete action
                      if (onDelete && actions.canDelete) {
                        hasActions.push(
                          <DropdownMenuItem 
                            key="delete"
                            onClick={async () => {
                              setLoadingActions(prev => ({ ...prev, [post.id]: 'delete' }))
                              try {
                                await onDelete(post.id)
                              } finally {
                                setLoadingActions(prev => {
                                  const { [post.id]: _, ...rest } = prev
                                  return rest
                                })
                              }
                            }}
                            className="text-destructive"
                            disabled={loadingActions[post.id] === 'delete'}
                          >
                            {loadingActions[post.id] === 'delete' ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Deleting...
                              </>
                            ) : (
                              <>
                                <Trash className="h-4 w-4 mr-2" />
                                Delete
                              </>
                            )}
                          </DropdownMenuItem>
                        );
                      }
                      
                      // If no actions available for this status
                      if (hasActions.length === 0) {
                        return (
                          <DropdownMenuItem disabled>
                            <span className="text-muted-foreground">
                              {post.status === POST_STATUS.PROCESSING ? 'Processing...' : 'No actions available'}
                            </span>
                          </DropdownMenuItem>
                        );
                      }
                      
                      return hasActions;
                    })()}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            
            <CardContent className="pb-3">
              {/* Images */}
              {post.image_urls && post.image_urls.length > 0 && (
                <div className="mb-3">
                  {post.image_urls.length === 1 ? (
                    <img
                      src={post.image_urls[0]}
                      alt="Post image"
                      className="w-full h-48 object-cover rounded-md"
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-1 rounded-md overflow-hidden">
                      {(post.image_urls || []).slice(0, 4).map((url, index) => (
                        <div key={index} className="relative">
                          <img
                            src={url}
                            alt={`Image ${index + 1}`}
                            className="w-full h-24 object-cover"
                          />
                          {index === 3 && post.image_urls && post.image_urls.length > 4 && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <span className="text-white font-semibold">
                                +{(post.image_urls?.length || 0) - 4}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Caption */}
              <p className="text-sm line-clamp-3">
                {post.caption || post.content || 'No caption'}
              </p>
              
              {/* Hashtags */}
              {post.hashtags && post.hashtags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {(post.hashtags || []).slice(0, 3).map((tag) => (
                    <span key={tag} className="text-xs text-blue-600">
                      {tag}
                    </span>
                  ))}
                  {post.hashtags && post.hashtags.length > 3 && (
                    <span className="text-xs text-muted-foreground">
                      +{post.hashtags.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </CardContent>
            
            <CardFooter className="pt-3 pb-3 text-xs text-muted-foreground">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {post.published_at ? (
                    <span>Published {format(new Date(post.published_at), 'MMM d, yyyy')}</span>
                  ) : post.scheduled_time ? (
                    <>
                      <span>Scheduled for {format(new Date(post.scheduled_time), 'MMM d, yyyy')}</span>
                      {post.status === POST_STATUS.SCHEDULED && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge variant="outline" className="ml-2">
                                <Clock className="h-3 w-3 mr-1" />
                                {getTimeRemaining(post.scheduled_time)}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{format(new Date(post.scheduled_time), 'PPp')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </>
                  ) : (
                    <span>Created {format(new Date(post.created_at), 'MMM d, yyyy')}</span>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {post.status === POST_STATUS.PROCESSING && (
                    <Badge 
                      variant="secondary" 
                      className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 animate-pulse"
                    >
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Publishing...
                    </Badge>
                  )}
                  {post.error_message && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge variant="destructive" className="text-xs">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Error
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">{post.error_message}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
            </CardFooter>
          </Card>
        )
      })}
    </div>
  )
}